import { RouterProvider } from "react-router-dom"
import { router } from "./routes"
import { Providers } from "./providers"
import { useEffect, useState, useCallback } from "react"
import { useTheme } from "@/hooks/useTheme"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { useAuthStore } from "@/stores/authStore"
import { useNotifications } from "@/hooks/useNotifications"
import { NotificationToastContainer } from "@/components/NotificationToast"

export function App() {
  // Initialize theme on app load
  useTheme()

  const [sessionToast, setSessionToast] = useState(false)
  const clearAuth = useAuthStore((s) => s.clearAuth)

  // Handle foreground push notifications by dispatching custom events for the toast container
  const handleNotification = useCallback((payload: { title?: string; body?: string; data?: Record<string, string> }) => {
    window.dispatchEvent(
      new CustomEvent("ezer:notification", {
        detail: {
          id: `notif_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          title: payload.title,
          body: payload.body,
          action: payload.data?.action,
        },
      })
    )
  }, [])

  useNotifications(handleNotification)

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(() => {}, () => {})
    }
  }, [])

  // Listen for session-expired events
  useEffect(() => {
    const handler = () => {
      clearAuth()
      localStorage.removeItem("ezer_session")
      localStorage.removeItem("ezer-auth-store")
      setSessionToast(true)
    }
    window.addEventListener("ezer:session-expired", handler)
    return () => window.removeEventListener("ezer:session-expired", handler)
  }, [clearAuth])

  // Auto-dismiss the session toast after 5 seconds
  useEffect(() => {
    if (!sessionToast) return
    const timer = setTimeout(() => setSessionToast(false), 5000)
    return () => clearTimeout(timer)
  }, [sessionToast])

  return (
    <ErrorBoundary>
      <Providers>
        <RouterProvider router={router} />
        <NotificationToastContainer />
        {sessionToast && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] bg-destructive text-destructive-foreground px-4 py-2 rounded-lg shadow-lg text-sm font-medium animate-in fade-in slide-in-from-top-2">
            Session expired. Please sign in again.
          </div>
        )}
      </Providers>
    </ErrorBoundary>
  )
}
